"use client";

import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

import GailIcon from "@/components/GailIcon";
import Logo from "@/components/Logo";
import { Icon, type IconNameExtended } from "@/components/library/Icon";
import { joinClasses } from "@/puck/landing/shared";
import type { LpGptPageMockupBlockProps } from "@/puck/types";

/** Fallback icon when a suggestion card has no icon configured. */
const DEFAULT_CARD_ICON: IconNameExtended = "file-lines";

export const GPT_PAGE_MOCKUP_DEFAULT_PROPS: LpGptPageMockupBlockProps = {
  greeting: "Good afternoon! How can I help?",
  inputPrompts: [
    { text: "Generate a COI for Riverside Logistics" },
    { text: "Summarize the loss runs for this account" },
    { text: "What changed on this policy at renewal?" },
    { text: "Draft an endorsement review for the added vehicle" },
  ],
  modelLabel: "Gail",
  searchPlaceholder: "Search...",
  suggestions: [
    {
      icon: "clipboard-list",
      title: "Review Claims",
      description: "Summarize claims with timeline and next steps",
    },
    {
      icon: "arrows-rotate",
      title: "Policy Renewal Review",
      description: "Compare expiring vs. renewal terms for changes",
    },
    {
      icon: "chart-line",
      title: "Loss Run Analysis",
      description: "Analyze loss runs for trends, ratios, and risk",
    },
    {
      icon: "file-lines",
      title: "Generate COI",
      description: "Create certificates of insurance from policy data",
    },
    {
      icon: "file-pen",
      title: "Endorsement Review",
      description: "Review endorsements for coverage changes and gaps",
    },
    {
      icon: "scale-balanced",
      title: "Compare Policies",
      description: "Compare coverage options across policies side by side",
    },
  ],
};

const GPT_PAGE_MOCKUP_ZOOM_SCALE: Record<
  NonNullable<LpGptPageMockupBlockProps["zoom"]>,
  number
> = {
  none: 1,
  "90": 0.9,
  "85": 0.85,
  "80": 0.8,
  "75": 0.75,
  "50": 0.5,
};

export function getGptPageMockupScale(
  zoom?: LpGptPageMockupBlockProps["zoom"],
): number {
  return GPT_PAGE_MOCKUP_ZOOM_SCALE[zoom ?? "none"] ?? 1;
}

export function getGptPageMockupZoomStyle(
  zoom?: LpGptPageMockupBlockProps["zoom"],
): React.CSSProperties | undefined {
  const scale = getGptPageMockupScale(zoom);
  if (scale === 1) {
    return undefined;
  }

  // `zoom` scales the mockup like a smaller browser viewport. Expanding width before
  // zoom keeps the visual footprint full-width inside the parent column.
  return {
    zoom: scale,
    width: `${100 / scale}%`,
    marginInline: "auto",
  };
}

const TYPING_SPEED_MS = 55;
const DELETING_SPEED_MS = 30;
const HOLD_MS = 1900;
const NEXT_PROMPT_DELAY_MS = 400;

type TypePhase = "typing" | "holding" | "deleting";

function getTypingDelay(phase: TypePhase, displayLength: number): number {
  if (phase === "holding") return HOLD_MS;
  if (phase === "deleting") {
    return displayLength === 0 ? NEXT_PROMPT_DELAY_MS : DELETING_SPEED_MS;
  }
  return TYPING_SPEED_MS;
}

function TrafficLights() {
  return (
    <div className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-full bg-red-400" />
      <span className="h-3 w-3 rounded-full bg-amber-400" />
      <span className="h-3 w-3 rounded-full bg-green-500" />
    </div>
  );
}

/** Types each prompt character-by-character, holds, deletes, then advances. */
function TypedPrompt({ prompts }: Readonly<{ prompts: string[] }>) {
  const shouldReduceMotion = useReducedMotion();
  const phrases = prompts.length > 0 ? prompts : [""];
  const [index, setIndex] = useState(0);
  const [display, setDisplay] = useState("");
  const [phase, setPhase] = useState<TypePhase>("typing");
  const current = phrases[index % phrases.length] ?? "";

  useEffect(() => {
    if (shouldReduceMotion) return;

    const delay = getTypingDelay(phase, display.length);

    const timeout = globalThis.setTimeout(() => {
      if (phase === "typing") {
        if (display === current) {
          setPhase(phrases.length > 1 ? "holding" : "typing");
          return;
        }
        setDisplay(current.slice(0, display.length + 1));
        return;
      }
      if (phase === "holding") {
        setPhase("deleting");
        return;
      }
      if (display.length === 0) {
        setIndex((value) => (value + 1) % phrases.length);
        setPhase("typing");
        return;
      }
      setDisplay(current.slice(0, display.length - 1));
    }, delay);

    return () => globalThis.clearTimeout(timeout);
  }, [phase, display, current, phrases.length, shouldReduceMotion]);

  return (
    <span className="font-monument text-sm text-black/80 md:text-base">
      {shouldReduceMotion ? current : display}
      {shouldReduceMotion ? null : (
        <span className="ml-0.5 inline-block animate-pulse text-gail-primary">
          |
        </span>
      )}
    </span>
  );
}

function NavItem({
  icon,
  label,
  active,
}: Readonly<{ icon: IconNameExtended; label: string; active?: boolean }>) {
  return (
    <span
      className={joinClasses(
        "flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 font-monument text-[0.8125rem]",
        active
          ? "bg-gail-primary/10 font-medium text-gail-primary"
          : "text-black/80",
      )}
    >
      <Icon
        icon={icon}
        className={joinClasses(
          "h-4 w-4",
          active ? "text-gail-primary" : "text-black/55",
        )}
      />
      <span className="truncate">{label}</span>
    </span>
  );
}

function NavGroup({
  label,
  expanded,
  children,
}: Readonly<{
  label: string;
  expanded?: boolean;
  children?: React.ReactNode;
}>) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 px-2.5 pt-3 pb-0.5">
        <span className="font-monument text-xs font-medium text-black/60">
          {label}
        </span>
        <Icon
          icon={expanded ? "chevron-up" : "chevron-down"}
          className="h-2.5 w-2.5 text-black/40"
        />
      </div>
      {children}
    </div>
  );
}

function SuggestionCard({
  icon,
  title,
  description,
}: Readonly<{ icon: IconNameExtended; title: string; description: string }>) {
  return (
    <div className="rounded-xl border border-black/[0.07] bg-white p-3.5 text-left transition-colors hover:border-gail-primary/30">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-black/[0.04] text-black/55">
        <Icon icon={icon} className="h-4 w-4" />
      </span>
      <p className="mt-3 font-monument text-sm font-medium text-black">
        {title}
      </p>
      <p className="mt-1 font-monument text-xs leading-snug text-black/60">
        {description}
      </p>
    </div>
  );
}

export function LpGptPageMockupBlock({
  greeting,
  inputPrompts,
  modelLabel,
  searchPlaceholder,
  suggestions,
  zoom,
  embedded,
}: Readonly<LpGptPageMockupBlockProps & { embedded?: boolean }>) {
  const prompts = (inputPrompts ?? [])
    .map((prompt) => prompt?.text)
    .filter((text): text is string => typeof text === "string" && text !== "");
  const scale = getGptPageMockupScale(zoom);
  const isZoomed = scale !== 1;
  const zoomStyle = getGptPageMockupZoomStyle(zoom);

  const frame = (
    <div
      aria-hidden
      className="rounded-[1.75rem] bg-gradient-to-br from-indigo-200 via-indigo-100 to-indigo-50 p-5 md:p-10"
    >
      <div className="overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_40px_80px_-40px_rgba(30,50,120,0.45)]">
        <div className="flex items-center gap-2 border-b border-black/[0.06] px-4 py-3">
          <TrafficLights />
          <div className="ml-2 hidden items-center gap-1.5 text-black/30 md:flex">
            <Icon icon="chevron-left" className="h-4 w-4" />
            <Icon icon="chevron-right" className="h-4 w-4" />
            <Icon icon="arrow-rotate-right" className="ml-1 h-3.5 w-3.5" />
          </div>
          <div className="mx-3 flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-black/[0.04] px-3 py-1.5">
            <Icon icon="lock" className="h-3 w-3 text-black/40" />
            <span className="truncate font-monument text-xs text-black/70">
              app.meetgail.com
            </span>
          </div>
          <span className="h-6 w-6 shrink-0 rounded-full bg-gail-primary" />
        </div>

        <div className="flex min-h-[28rem]">
          <aside className="hidden w-52 shrink-0 flex-col border-r border-black/[0.06] bg-white p-3 md:flex">
            <div className="flex items-center justify-between px-1.5 py-1">
              <Logo className="h-5 w-auto" />
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="text-black/35"
              >
                <path d="M4 5v14" />
                <path d="M20 12H9" />
                <path d="m13 8-4 4 4 4" />
              </svg>
            </div>

            <div className="mt-3 flex items-center justify-between rounded-lg bg-black/[0.04] px-2.5 py-1.5">
              <span className="flex items-center gap-2 font-monument text-[0.8125rem] text-black/60">
                <Icon icon="magnifying-glass" className="h-3.5 w-3.5" />
                {searchPlaceholder}
              </span>
              <kbd className="rounded border border-black/10 bg-white px-1.5 py-0.5 font-mono text-[0.625rem] text-black/60">
                ⌘K
              </kbd>
            </div>

            <nav className="mt-2 flex-1">
              <NavGroup label="Data & Analytics" expanded>
                <NavItem icon="gauge" label="Dashboard" />
              </NavGroup>
              <NavGroup label="GailGPT" expanded>
                <NavItem icon="comment" label="New Chat" active />
                <NavItem icon="clock-rotate-left" label="History" />
                <NavItem icon="wand-magic-sparkles" label="Skills" />
              </NavGroup>
              <NavGroup label="Communications" />
              <NavGroup label="General" />
            </nav>

            <div className="mt-4 rounded-lg border border-black/[0.07] p-2.5">
              <div className="flex items-center justify-between">
                <span className="font-monument text-xs text-black/60">
                  Trial usage
                </span>
                <span className="flex items-center gap-1 font-monument text-xs text-black/70">
                  2%
                  <Icon icon="chevron-down" className="h-2 w-2" />
                </span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-black/[0.06]">
                <div className="h-full w-[2%] min-w-1 rounded-full bg-gail-primary" />
              </div>
              <span className="mt-2.5 flex items-center justify-center rounded-full border border-gail-primary/40 px-3 py-1 font-monument text-xs font-medium text-gail-primary">
                Upgrade
              </span>
            </div>

            <div className="mt-3 space-y-0.5">
              <NavItem icon="book" label="Gail University" />
              <NavItem icon="gear" label="Settings" />
              <NavItem icon="circle-question" label="Help & Support" />
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col px-5 py-8 md:px-10 md:py-12">
            <div className="mx-auto w-full max-w-2xl">
              <div className="flex items-center justify-center gap-3">
                <span className="[&>svg]:h-7 [&>svg]:w-7">
                  <GailIcon isBlue />
                </span>
                <h3 className="font-martina text-2xl leading-tight text-black md:text-[1.75rem]">
                  {greeting}
                </h3>
              </div>

              <div className="mt-7 rounded-2xl border border-black/10 bg-white p-4 shadow-[0_18px_40px_-30px_rgba(13,78,176,0.45)]">
                <div className="min-h-[2.5rem]">
                  <TypedPrompt prompts={prompts} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <Icon icon="plus" className="h-5 w-5 text-black/40" />
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 font-monument text-[0.8125rem] text-black/70">
                      {modelLabel}
                      <Icon icon="chevron-down" className="h-2.5 w-2.5" />
                    </span>
                    <Icon
                      icon="arrow-up"
                      className="h-5 w-5 text-black/35"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {suggestions.map((card, index) => (
                  <SuggestionCard
                    key={`suggestion-${index}`}
                    icon={
                      (card.icon as IconNameExtended) || DEFAULT_CARD_ICON
                    }
                    title={card.title}
                    description={card.description}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return frame;
  }

  return (
    <section
      className={joinClasses(
        "flex w-full justify-center",
        isZoomed ? "px-0 pt-4 pb-6 md:pt-6" : "px-4 pt-10 pb-16 md:pt-14",
      )}
    >
      <div
        className={joinClasses("w-full", isZoomed ? "max-w-full" : "max-w-5xl")}
        style={zoomStyle}
      >
        {frame}
      </div>
    </section>
  );
}
